import { useCallback, useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  dismissPromo,
  selectShouldShowEhrPromo,
  snoozePromo,
} from '@features/ehr-extension-promo';
import { useEntitlement } from '@features/entitlements';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useModal } from '@store/hooks/useModal';

export type EhrPromoTriggerAction = 'copy-all' | 'copy-selected' | 'copy-section';

/**
 * Hook to manage the EHR extension promo popup.
 *
 * Dismissal state is persisted via Redux persist (scoped storage).
 *
 * - User clicks X (close): permanently dismissed
 * - User clicks "Maybe later": snoozed for 2 weeks
 * - User clicks "Learn More": permanently dismissed + navigates to settings
 * - User has a purchased/admin-granted unlimited add-on: never shown.
 *   Trial users with the default synthetic trial grant (assignment_source
 *   === 'trial') still see the promo — they haven't committed to the add-on.
 */
export function useEhrExtensionPromo() {
  const { open } = useModal();
  const shouldShow = useAppSelector(selectShouldShowEhrPromo);
  const isGateEnabled = useFeatureGate(FeatureGates.EHR_EXTENSION_ADDON);
  const { entitlement } = useEntitlement('ehr_note_copy');
  const hasUnlimited =
    !!entitlement &&
    (entitlement.quota === null || entitlement.quota === undefined);
  // Trial users get a synthetic `ehr_note_copy` entitlement with quota=null
  // tagged assignment_source='trial'. Only entitlements granted via purchase
  // ('stripe') or admin grant ('manual') count as a real paid add-on.
  const hasPaidAddon =
    hasUnlimited && entitlement!.assignment_source !== 'trial';
  const pendingRef = useRef(false);

  const showPromo = useCallback(
    (triggerAction: EhrPromoTriggerAction) => {
      if (!isGateEnabled || !shouldShow || hasPaidAddon) return;
      if (pendingRef.current) return;

      pendingRef.current = true;
      setTimeout(() => {
        pendingRef.current = false;
        open(MODAL_TYPES.EHR_EXTENSION_PROMO, { triggerAction, maxWidth: 'md' });
      }, 600);
    },
    [isGateEnabled, shouldShow, hasPaidAddon, open],
  );

  const wouldShowPromo = isGateEnabled && shouldShow && !hasPaidAddon;

  return { showPromo, wouldShowPromo };
}

/** Hook to get promo dispatch actions (for use in modal) */
export function useEhrPromoActions() {
  const dispatch = useAppDispatch();

  return {
    dismiss: useCallback(() => dispatch(dismissPromo()), [dispatch]),
    snooze: useCallback(() => dispatch(snoozePromo()), [dispatch]),
  };
}
